<script setup>
// SiteUp (backlog): Painel da IA reachable no settings do Captain — métricas da
// conta (ai_agent_metrics) + reports de erro (ai_agent_feedbacks) com resolver.
// Substitui o AgentMetricsSection órfão (que nunca era renderizado).
// SiteUp (operacao): período/inbox nas métricas, falhas hoje, tokens/custo,
// kill switch da conta e teste do motor de IA.
import { computed, ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import AiAgentMetricsAPI from 'dashboard/api/aiAgentMetrics';
import AiAgentFeedbacksAPI from 'dashboard/api/aiAgentFeedbacks';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import PropertyFollowupPanel from './PropertyFollowupPanel.vue';

const route = useRoute();
const { t } = useI18n();
const metrics = ref(null);
const feedbacks = ref([]);
const isFetching = ref(false);
const loadError = ref(null); // 'forbidden' | 'generic' | null

// SiteUp (operacao): filtros do painel. 'all' = comportamento antigo (tudo).
const period = ref('all');
const inboxId = ref('');
const periodOptions = computed(() => [
  {
    value: 'today',
    label: t('AI_AGENTS.PROPERTY_FOLLOWUPS.PERIODS.TODAY'),
  },
  {
    value: '7d',
    label: t('AI_AGENTS.PROPERTY_FOLLOWUPS.PERIODS.LAST_7_DAYS'),
  },
  {
    value: '30d',
    label: t('AI_AGENTS.PROPERTY_FOLLOWUPS.PERIODS.LAST_30_DAYS'),
  },
  {
    value: 'all',
    label: t('AI_AGENTS.PROPERTY_FOLLOWUPS.PERIODS.ALL'),
  },
]);
const inboxes = useMapGetter('inboxes/getInboxes');
let latestMetricsRequestId = 0;

// SiteUp (operacao): estado do kill switch / teste do motor.
const isTogglingPause = ref(false);
const isTestingEngine = ref(false);
const engineResult = ref(null); // { ok, model, latency_ms, error }

const assistantId = computed(() => Number(route.params.assistantId));
const backUrl = computed(() => ({
  name: 'captain_assistants_settings_index',
  params: { accountId: route.params.accountId, assistantId: assistantId.value },
}));

const timeAgo = dateStr => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

// SiteUp: status do pipeline report → proposta Copilot (não confundir com resolved).
const feedbackStatusLabel = fb => {
  if (fb.status === 'pending') {
    return t('GENERAL.AI_FEEDBACK_PIPELINE_PENDING');
  }
  if (fb.status === 'processing') {
    return t('GENERAL.AI_FEEDBACK_PIPELINE_PROCESSING');
  }
  if (fb.status === 'proposal_generated') {
    return t('GENERAL.AI_FEEDBACK_PIPELINE_PROPOSAL_GENERATED');
  }
  if (fb.status === 'no_action') {
    return t('GENERAL.AI_FEEDBACK_PIPELINE_NO_ACTION');
  }
  if (fb.status === 'processed') {
    // DB has no separate "error" enum — job failures land as processed + processing_error.
    return fb.processing_error
      ? t('GENERAL.AI_FEEDBACK_PIPELINE_ERROR')
      : t('GENERAL.AI_FEEDBACK_PIPELINE_PROCESSED');
  }
  return fb.status || '—';
};

const feedbackMetadata = fb =>
  [
    fb.user_name || t('GENERAL.AI_FEEDBACK_USER_FALLBACK'),
    timeAgo(fb.created_at),
    feedbackStatusLabel(fb),
    fb.assistant_response_id
      ? t('GENERAL.AI_FEEDBACK_RESPONSE_ID', {
          id: fb.assistant_response_id,
        })
      : null,
    fb.processing_error,
  ]
    .filter(Boolean)
    .join(' · ');

const fmt = n => new Intl.NumberFormat('pt-BR').format(n || 0);

const metricsParams = () => {
  const params = { period: period.value };
  if (inboxId.value) params.inbox_id = inboxId.value;
  return params;
};

const fetchMetrics = async () => {
  latestMetricsRequestId += 1;
  const requestId = latestMetricsRequestId;
  try {
    const response = await AiAgentMetricsAPI.get(metricsParams());
    if (requestId !== latestMetricsRequestId) return false;

    metrics.value = response.data;
    return true;
  } catch (error) {
    if (requestId !== latestMetricsRequestId) return false;
    metrics.value = null;
    throw error;
  }
};

const fetchAll = async () => {
  isFetching.value = true;
  loadError.value = null;
  try {
    const [, f] = await Promise.all([
      fetchMetrics(),
      AiAgentFeedbacksAPI.get(),
    ]);
    feedbacks.value = f.data || [];
  } catch (error) {
    // Não engolir o erro: 403 (endpoints são admin-only) vira um aviso claro de
    // permissão; demais falhas mostram um alerta em vez de uma página em branco.
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      loadError.value = 'forbidden';
    } else {
      loadError.value = 'generic';
      useAlert('Não foi possível carregar o painel da IA.');
    }
  } finally {
    isFetching.value = false;
  }
};

// SiteUp (operacao): trocar período/caixa refaz só as métricas (feedbacks são globais).
watch([period, inboxId], async () => {
  if (loadError.value) return;
  try {
    await fetchMetrics();
  } catch {
    useAlert('Não foi possível atualizar as métricas.');
  }
});

const resolveFeedback = async fb => {
  try {
    await AiAgentFeedbacksAPI.resolve(fb.id, !fb.resolved);
    fb.resolved = !fb.resolved;
  } catch {
    useAlert('Não foi possível atualizar.');
  }
};

// SiteUp (operacao): kill switch — pausa/retoma a IA da conta inteira, com
// confirmação explícita (é o botão de incidente).
const toggleAccountPause = async () => {
  const pausing = !metrics.value?.captain_paused;
  const message = pausing
    ? 'Pausar a IA da conta INTEIRA? Nenhuma caixa terá resposta automática até reativar.'
    : 'Reativar a IA da conta? As caixas voltam a responder automaticamente.';
  // eslint-disable-next-line no-alert
  if (!window.confirm(message)) return;

  isTogglingPause.value = true;
  try {
    const { data } = await AiAgentMetricsAPI.togglePause(pausing);
    if (metrics.value) metrics.value.captain_paused = data.captain_paused;
    useAlert(
      data.captain_paused ? 'IA da conta pausada.' : 'IA da conta reativada.'
    );
  } catch {
    useAlert('Não foi possível alterar a pausa da IA.');
  } finally {
    isTogglingPause.value = false;
  }
};

// SiteUp (operacao): teste do motor — 1 completion mínima com o modelo real.
const testEngine = async () => {
  isTestingEngine.value = true;
  engineResult.value = null;
  try {
    const { data } = await AiAgentMetricsAPI.engineHealth();
    engineResult.value = data;
  } catch (error) {
    engineResult.value = {
      ok: false,
      error: error?.response?.data?.error || 'Falha ao testar o motor de IA.',
    };
  } finally {
    isTestingEngine.value = false;
  }
};

onMounted(fetchAll);
</script>

<template>
  <PageLayout
    header-title="Painel da IA"
    :is-fetching="isFetching"
    :back-url="backUrl"
    :show-know-more="false"
    :show-pagination-footer="false"
    :show-assistant-switcher="false"
  >
    <template #body>
      <SettingsHeader
        heading="Painel da IA"
        description="Métricas do agente e os erros reportados pelos atendentes (correções que entram no aprendizado da IA)."
      />

      <div
        v-if="loadError === 'forbidden'"
        class="flex items-start gap-2 mt-7 px-3 py-2 rounded-xl bg-n-amber-2 text-n-amber-11 text-sm"
      >
        <span class="i-ri-lock-line size-4 shrink-0 mt-0.5" />
        <span>
          O Painel da IA é restrito a administradores. Peça a um administrador da
          conta para acessar as métricas e os reports de erro.
        </span>
      </div>

      <div
        v-else-if="loadError === 'generic'"
        class="flex items-center justify-between gap-3 mt-7 px-3 py-2 rounded-xl bg-n-slate-2 text-n-slate-11 text-sm"
      >
        <span>Não foi possível carregar o painel da IA.</span>
        <button
          type="button"
          class="rounded-md px-2.5 py-1 text-xs font-medium bg-n-teal-9/15 text-n-teal-11"
          @click="fetchAll"
        >
          Tentar de novo
        </button>
      </div>

      <!-- SiteUp (operacao): kill switch + teste do motor, no topo (resposta a incidente) -->
      <div
        v-if="!loadError && metrics"
        class="flex flex-wrap items-center gap-3 mt-7 rounded-2xl border p-3"
        :class="
          metrics.captain_paused
            ? 'border-n-amber-7 bg-n-amber-2'
            : 'border-n-weak'
        "
      >
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-n-slate-12">
            <span
              class="inline-block size-2 rounded-full mr-1.5 align-middle"
              :class="metrics.captain_paused ? 'bg-n-amber-9' : 'bg-n-teal-9'"
            />
            {{
              metrics.captain_paused
                ? 'IA da conta PAUSADA — nenhuma caixa responde automaticamente.'
                : 'IA da conta ativa.'
            }}
          </p>
          <p v-if="engineResult" class="text-xs mt-1" :class="engineResult.ok ? 'text-n-teal-11' : 'text-n-ruby-11'">
            <template v-if="engineResult.ok">
              Motor OK · {{ engineResult.model }} · {{ engineResult.latency_ms }}ms
            </template>
            <template v-else>
              Motor com erro: {{ engineResult.error }}
            </template>
          </p>
        </div>
        <button
          type="button"
          class="shrink-0 rounded-md px-3 py-1.5 text-xs font-medium border border-n-weak text-n-slate-11 hover:bg-n-alpha-2"
          :disabled="isTestingEngine"
          @click="testEngine"
        >
          {{ isTestingEngine ? 'Testando…' : 'Testar motor de IA' }}
        </button>
        <button
          type="button"
          class="shrink-0 rounded-md px-3 py-1.5 text-xs font-semibold"
          :class="
            metrics.captain_paused
              ? 'bg-n-teal-9/15 text-n-teal-11'
              : 'bg-n-ruby-9/15 text-n-ruby-11'
          "
          :disabled="isTogglingPause"
          @click="toggleAccountPause"
        >
          {{ metrics.captain_paused ? 'Reativar IA da conta' : 'Pausar IA da conta' }}
        </button>
      </div>

      <!-- SiteUp (operacao): seletor de período e de caixa -->
      <div v-if="!loadError && metrics" class="flex flex-wrap items-center gap-2 mt-4">
        <div class="flex rounded-xl border border-n-weak overflow-hidden">
          <button
            v-for="opt in periodOptions"
            :key="opt.value"
            type="button"
            class="px-2.5 py-1 text-xs font-medium"
            :class="
              period === opt.value
                ? 'bg-n-teal-9/15 text-n-teal-11'
                : 'text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="period = opt.value"
          >
            {{ opt.label }}
          </button>
        </div>
        <select
          v-model="inboxId"
          class="!mb-0 !h-7 !py-0 !px-2 !text-xs !w-auto rounded-xl border border-n-weak bg-transparent text-n-slate-11"
        >
          <option value="">Todas as caixas</option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
      </div>

      <div v-if="!loadError && metrics" class="grid grid-cols-3 gap-3 mt-4">
        <div class="rounded-2xl border border-n-weak p-4 text-center">
          <p class="text-2xl font-extrabold text-n-slate-12">{{ metrics.active_conversations }}</p>
          <p class="text-xs text-n-slate-10">Conversas com IA ativa</p>
        </div>
        <div class="rounded-2xl border border-n-weak p-4 text-center">
          <p class="text-2xl font-extrabold text-n-teal-11">{{ metrics.ai_messages_sent }}</p>
          <p class="text-xs text-n-slate-10">Mensagens enviadas pela IA</p>
        </div>
        <div class="rounded-2xl border border-n-weak p-4 text-center">
          <p class="text-2xl font-extrabold text-n-amber-11">{{ metrics.feedbacks_count }}</p>
          <p class="text-xs text-n-slate-10">Reports de erro</p>
        </div>
      </div>

      <!-- SiteUp (operacao): falhas técnicas hoje + tokens/uso (contadores do pipeline) -->
      <div v-if="!loadError && metrics" class="grid grid-cols-3 gap-3 mt-3">
        <div
          class="rounded-2xl border p-4 text-center"
          :class="metrics.failures_today ? 'border-n-ruby-7 bg-n-ruby-2' : 'border-n-weak'"
        >
          <p
            class="text-2xl font-extrabold"
            :class="metrics.failures_today ? 'text-n-ruby-11' : 'text-n-slate-12'"
          >
            {{ metrics.failures_today || 0 }}
          </p>
          <p class="text-xs text-n-slate-10">Falhas técnicas hoje</p>
        </div>
        <div class="rounded-2xl border border-n-weak p-4 text-center">
          <p class="text-2xl font-extrabold text-n-slate-12">
            {{ fmt((metrics.usage?.today?.input_tokens || 0) + (metrics.usage?.today?.output_tokens || 0)) }}
          </p>
          <p class="text-xs text-n-slate-10">
            Tokens hoje ({{ fmt(metrics.usage?.today?.runs) }} respostas)
            <template v-if="metrics.usage?.today?.estimated_cost != null">
              · ~R$ {{ metrics.usage.today.estimated_cost }}
            </template>
          </p>
        </div>
        <div class="rounded-2xl border border-n-weak p-4 text-center">
          <p class="text-2xl font-extrabold text-n-slate-12">
            {{ fmt((metrics.usage?.last_7_days?.input_tokens || 0) + (metrics.usage?.last_7_days?.output_tokens || 0)) }}
          </p>
          <p class="text-xs text-n-slate-10">
            Tokens 7 dias ({{ fmt(metrics.usage?.last_7_days?.runs) }} respostas)
            <template v-if="metrics.usage?.last_7_days?.estimated_cost != null">
              · ~R$ {{ metrics.usage.last_7_days.estimated_cost }}
            </template>
          </p>
        </div>
      </div>

      <!-- SiteUp (#3): performance da IA (derivada dos dados existentes) -->
      <div v-if="metrics && metrics.ai_conversations" class="grid grid-cols-3 gap-3 mt-3">
        <div class="rounded-2xl border border-n-weak p-4 text-center">
          <p class="text-2xl font-extrabold text-n-teal-11">{{ metrics.resolution_rate }}%</p>
          <p class="text-xs text-n-slate-10">
            Resolvidas pela IA ({{ metrics.ai_conversations }} conversas)
          </p>
        </div>
        <div class="rounded-2xl border border-n-weak p-4 text-center">
          <p class="text-2xl font-extrabold text-n-slate-12">{{ metrics.handoff_rate }}%</p>
          <p class="text-xs text-n-slate-10">Passou pra humano</p>
        </div>
        <div class="rounded-2xl border border-n-weak p-4 text-center">
          <p class="text-2xl font-extrabold text-n-amber-11">
            {{ metrics.csat_average != null ? metrics.csat_average : '—' }}
          </p>
          <p class="text-xs text-n-slate-10">
            CSAT médio{{ metrics.csat_count ? ` (${metrics.csat_count})` : '' }}
          </p>
        </div>
      </div>

      <!-- IA-8: qualidade média auto-avaliada (LLM-judge), só aparece se houver avaliações -->
      <div v-if="metrics && metrics.quality_count" class="grid grid-cols-1 gap-3 mt-3">
        <div class="rounded-2xl border border-n-weak p-4 text-center">
          <p class="text-2xl font-extrabold text-n-teal-11">
            {{ metrics.quality_average != null ? metrics.quality_average : '—'
            }}<span class="text-sm font-normal text-n-slate-10">/100</span>
          </p>
          <p class="text-xs text-n-slate-10">
            Qualidade média auto-avaliada ({{ metrics.quality_count }} respostas)
          </p>
        </div>
      </div>

      <PropertyFollowupPanel
        v-if="!loadError && metrics?.property_followups"
        :data="metrics.property_followups"
        :account-id="route.params.accountId"
        :period-code="metrics.period"
      />

      <div v-if="!loadError" class="flex flex-col gap-2 mt-6">
        <h4 class="text-xs font-medium text-n-slate-12">
          Reports de erro da IA (correções)
        </h4>
        <div v-if="feedbacks.length === 0" class="text-sm text-n-slate-11">
          Nenhum report ainda. Os atendentes registram erros pelo menu da resposta da IA.
        </div>
        <div
          v-for="fb in feedbacks"
          :key="fb.id"
          class="flex items-start gap-3 rounded-xl border border-n-weak p-3"
          :class="{ 'opacity-60': fb.resolved }"
        >
          <span class="i-ri-flag-line size-4 shrink-0 mt-0.5 text-n-ruby-10" />
          <div class="min-w-0 flex-1">
            <p
              class="text-sm text-n-slate-12"
              :class="{ 'line-through': fb.resolved }"
            >
              {{ fb.reason }}
            </p>
            <span class="text-[10px] text-n-slate-10">
              {{ feedbackMetadata(fb) }}
            </span>
          </div>
          <button
            type="button"
            class="shrink-0 rounded-md px-2.5 py-1 text-xs font-medium"
            :class="
              fb.resolved
                ? 'text-n-slate-11 hover:bg-n-alpha-2'
                : 'bg-n-teal-9/15 text-n-teal-11'
            "
            @click="resolveFeedback(fb)"
          >
            {{ fb.resolved ? 'Reabrir' : 'Resolver' }}
          </button>
        </div>
      </div>
    </template>
  </PageLayout>
</template>
